import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';

import { Button } from 'admin/components/atoms/Buttons';
import TextArea from 'admin/components/atoms/TextArea';
import LabeledSelect from 'admin/components/molecules/LabeledSelect';
import LabeledTextBox from 'admin/components/molecules/LabeledTextBox';
import LabeledCheckBox from 'admin/components/molecules/LabeledCheckBox';

import DateTimeRange from 'admin/components/atoms/DateTimeRange';
import DateTime from 'admin/components/atoms/DateTime';

import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';

import SetOptionsForm from './SetOptionsForm';
import { createSchemaFromObject } from './schema';

const FormWrapper = styled.div`
  padding: 0.5em 1em;
  margin: 1em 0;
  background: #f4f4f4;
  border-left: solid 6px green;
  box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.33);
  display: grid;

  ${Labeled} {
    display: grid;
    grid-template-columns: 1.5fr 4fr;
    grid-template-rows: 50px;
    justify-items: left;
    margin-top: 10px;
    align-items: center;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${TextArea} {
    width: 400px;
    resize: both;
  }
`;

const InputForm = ({
  index, schema, setSchema, moveUpSchema, moveDownSchema, deleteSchema,
}) => {
  const formTypeOptions = [
    { label: '自由記述', value: 'text' },
    { label: '日付', value: 'date_select' },
    { label: '単一選択', value: 'radio' },
    { label: '複数選択', value: 'checkbox' },
    { label: '数字', value: 'number' },
    { label: '整数（半角数値）', value: 'integer' },
    { label: '郵便番号', value: 'zip_code' },
    { label: 'メールアドレス', value: 'email' },
    { label: 'URL', value: 'url' },
    { label: '都道府県', value: 'prefectures' },
    { label: '正規表現を使う', value: 'regex' },
  ];
  const prefecturesOptions = [
    { label: '選択しない', value: null },
    { label: '北海道', value: '北海道' },
    { label: '青森県', value: '青森県' },
    { label: '岩手県', value: '岩手県' },
    { label: '宮城県', value: '宮城県' },
    { label: '秋田県', value: '秋田県' },
    { label: '山形県', value: '山形県' },
    { label: '福島県', value: '福島県' },
    { label: '茨城県', value: '茨城県' },
    { label: '栃木県', value: '栃木県' },
    { label: '群馬県', value: '群馬県' },
    { label: '埼玉県', value: '埼玉県' },
    { label: '千葉県', value: '千葉県' },
    { label: '東京都', value: '東京都' },
    { label: '神奈川県', value: '神奈川県' },
    { label: '新潟県', value: '新潟県' },
    { label: '富山県', value: '富山県' },
    { label: '石川県', value: '石川県' },
    { label: '福井県', value: '福井県' },
    { label: '山梨県', value: '山梨県' },
    { label: '長野県', value: '長野県' },
    { label: '岐阜県', value: '岐阜県' },
    { label: '静岡県', value: '静岡県' },
    { label: '愛知県', value: '愛知県' },
    { label: '三重県', value: '三重県' },
    { label: '滋賀県', value: '滋賀県' },
    { label: '京都府', value: '京都府' },
    { label: '大阪府', value: '大阪府' },
    { label: '兵庫県', value: '兵庫県' },
    { label: '奈良県', value: '奈良県' },
    { label: '和歌山県', value: '和歌山県' },
    { label: '鳥取県', value: '鳥取県' },
    { label: '島根県', value: '島根県' },
    { label: '岡山県', value: '岡山県' },
    { label: '広島県', value: '広島県' },
    { label: '山口県', value: '山口県' },
    { label: '徳島県', value: '徳島県' },
    { label: '香川県', value: '香川県' },
    { label: '愛媛県', value: '愛媛県' },
    { label: '高知県', value: '高知県' },
    { label: '福岡県', value: '福岡県' },
    { label: '佐賀県', value: '佐賀県' },
    { label: '長崎県', value: '長崎県' },
    { label: '熊本県', value: '熊本県' },
    { label: '大分県', value: '大分県' },
    { label: '宮崎県', value: '宮崎県' },
    { label: '鹿児島県', value: '鹿児島県' },
    { label: '沖縄県', value: '沖縄県' },
  ];

  const getIncludeText = (regex, type) => {
    if (regex !== undefined) {
      let newStr = regex.replace('^(?=.*(', '');
      const pos1 = newStr.indexOf('))');
      if (type === 'include') {
        return newStr.substring(0, pos1).replace(/\|/gi, ',');
      } else if (type === 'notInclude') {
        newStr = newStr.substring(pos1).replace('))(?!.*(', '');
        const pos2 = newStr.indexOf('))');
        return newStr.substring(0, pos2).replace(/\|/gi, ',');
      }
    }
    return '';
  };

  const [isRequired, setIsRequired] = useState(schema.required);
  const [selectedFormType, setSelectedFormType] =
        useState(formTypeOptions.find(ops => ops.value === schema.form_type));
  const [includeText, setIncludeText]
        = useState(schema.form_type === 'text' ? getIncludeText(schema.regex, 'include') : '');
  const [notIncludeText, setNotIncludeText]
        = useState(schema.form_type === 'text' ? getIncludeText(schema.regex, 'notInclude') : '');
  const [regex, setRegex] = useState(schema.regex);
  const [dateRange, setDateRange] = useState({
    from: schema.date_set_value == null ? null : moment(schema.date_set_value.start_at),
    to: schema.date_set_value == null ? null : moment(schema.date_set_value.end_at),
  });
  const [defaultDate, setDefaultDate]
        = useState(schema.date_set_value == null ? null : moment(schema.date_set_value.default_at));
  const [numberRange, setNumberRange] = useState({ min: null, max: null });

  const isTextFormType = formType => (
    formType === 'text' || formType === 'number' || formType === 'integer' ||
      formType === 'zip_code' || formType === 'email' || formType === 'url' ||
      formType === 'regex'
  );

  const updateSchema = (key, value) => {
    const newSchema = createSchemaFromObject(schema);
    newSchema.setValueFromKey(key, value);
    setSchema(newSchema);
  };

  const updateSchemas = (values) => {
    const newSchema = createSchemaFromObject(schema);
    const keys = Object.keys(values);
    keys.forEach(k => newSchema.setValueFromKey(k, values[k]));
    setSchema(newSchema);
  };

  const updateSchemaOptions = (options) => {
    updateSchema('options', options);
  };

  const onSelectFormType = (op) => {
    const formType = op.value;

    const defaultObjects = {
      form_type: formType,
      options: [],
      default: '',
      placeholder: '',
      prefix_label: '',
      regex: '',
      regex_error_message_ja: '',
      regex_error_message_en: '',
      required_reason: '',
      date_set_value: null,
    };

    updateSchemaOptions([]);
    setSelectedFormType(formTypeOptions.find(opt => opt.value === formType));
    setRegex('');

    if (formType === 'date_select') {
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'Date',
      }));
    } else if (formType === 'number') {
      setRegex('^[-]?([0-9０-９]+)(.[0-9０-９]+)?$');
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        regex: '^[-]?([0-9０-９]+)(.[0-9０-９]+)?$',
        regex_error_message_ja: '無効な数字の登録',
        regex_error_message_en: 'invalid number registration',
      }));
    } else if (formType === 'integer') {
      setRegex('^[+-]?\\d+$');
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        regex: '^[+-]?\\d+$',
        regex_error_message_ja: '無効な整数（半角数値）の登録',
        regex_error_message_en: 'invalid integer registration',
      }));
    } else if (formType === 'zip_code') {
      setRegex('[0-9]{5,9}');
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        regex: '[0-9]{5,9}',
        regex_error_message_ja: '無効な郵便番号の登録',
        regex_error_message_en: 'invalid zip code registration',
      }));
    } else if (formType === 'email') {
      setRegex('^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\\.[A-Za-z0-9]{1,}$');
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        regex: '^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\\.[A-Za-z0-9]{1,}$',
        regex_error_message_ja: '無効なEMailの登録',
        regex_error_message_en: 'invalid email registration',
      }));
    } else if (formType === 'url') {
      setRegex('https?://[\\w/:%#\\$&\\?\\(\\)~\\.=\\+\\-]+');
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        regex: 'https?://[\\w/:%#\\$&\\?\\(\\)~\\.=\\+\\-]+',
        regex_error_message_ja: '無効なURLの登録',
        regex_error_message_en: 'invalid url registration',
      }));
    } else if (formType === 'prefectures') {
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
        options: prefecturesOptions,
      }));
    } else if (formType === 'radio' || formType === 'checkbox') {
      updateSchemas(Object.assign({}, defaultObjects, {
        options: [{ label: '', value: '' }],
        default: null,
      }));
    } else {
      updateSchemas(Object.assign({}, defaultObjects, {
        type: 'String',
      }));
    }
  };

  const handleChangeOrder = (e, type) => {
    e.preventDefault();

    if (type === 'up') {
      moveUpSchema(schema);
    } else if (type === 'down') {
      moveDownSchema(schema);
    }
  };

  const onCheckRequired = (op) => {
    setIsRequired(op);
    if (op === false) {
      updateSchemas({
        required: false,
        required_reason: '',
      });
    } else {
      updateSchemas({
        required: true,
      });
    }
  };

  const regexSpecialChars = str => (
    String(str).replace(/\\/g, '\\')
      .replace(/\*/g, '\\*')
      .replace(/\+/g, '\\+')
      .replace(/\./g, '\\.')
      .replace(/\?/g, '\\?')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\[/g, '\\[')
      .replace(/\]/g, '\\]')
      .replace(/\^/g, '\\^')
      .replace(/\$/g, '\\$')
      .replace(/\\-/g, '\\-')
      .replace(/\|/g, '\\|')
      .replace(/\//g, '\\/')
  );

  const onSetIncludeText = (value) => {
    const str = regexSpecialChars(value);
    const notIncStr = notIncludeText.length !== 0 ? `(?!.*(${notIncludeText.replace(/,/g, '|')}))` : '';
    if (str.length !== 0 || notIncStr.length !== 0) {
      updateSchema('regex', `^(?=.*(${str.replace(/,/g, '|')}))${notIncStr}.*$`);
      setRegex(`^(?=.*(${str.replace(/,/g, '|')}))${notIncStr}.*$`);
    } else {
      updateSchema('regex', '');
    }
    setIncludeText(str);
  };

  const onSetNotIncludeText = (value) => {
    const str = regexSpecialChars(value);
    const incStr = includeText.length !== 0 ? `(?=.*(${includeText.replace(/,/g, '|')}))` : '';
    if (str.length !== 0 || incStr.length !== 0) {
      updateSchema('regex', `^${incStr}(?!.*(${str.replace(/,/g, '|')})).*$`);
      setRegex(`^${incStr}(?!.*(${str.replace(/,/g, '|')})).*$`);
    } else {
      updateSchema('regex', '');
    }
    setNotIncludeText(str);
  };

  const getRegexErrMessageEn = (regexErrMessageEn, regexErrType) => {
    if (regexErrMessageEn !== undefined) {
      return regexErrMessageEn;
    }
    return regexErrType !== undefined ? regexErrType.replace(/_/g, ' ') : '';
  };

  /* Options function */
  const addOptions = () => {
    const newOptions = [...schema.options, { label: '', value: '' }];
    updateSchemaOptions(newOptions);
  };

  const editOptions = (newItem, i) => {
    const newOptions = [...schema.options];
    Object.assign(newOptions[i], newItem);
    updateSchemaOptions(newOptions);
  };

  const deleteOptions = (i) => {
    const oldOptions = [...schema.options];
    if (i !== -1) {
      const newOptions = oldOptions.filter(opt => opt !== oldOptions[i]);
      updateSchemaOptions(newOptions);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    deleteSchema(index);
  };

  const handleNumberRange = (type, num) => {
    if (num !== null) {
      let ret;
      if (type === 'min') {
        ret = { ...numberRange, min: num };
        setNumberRange(ret);
      } else if (type === 'max') {
        ret = { ...numberRange, max: num };
        setNumberRange(ret);
      }
      updateSchema('number_range', ret);
    }
  };

  const handleDefaultDate = (date) => {
    if (date !== null) {
      setDefaultDate(date.endOf('day'));
      updateSchemas({
        date_set_value: {
          default_at: date,
          start_at: dateRange[0],
          end_at: dateRange[1],
        },
      });
    }
  };

  const handleDateRange = (date) => {
    const startDate = date.from;
    const endDate = date.to;

    setDateRange({
      from: startDate ? startDate.startOf('day') : null,
      to: endDate ? endDate.endOf('day') : null,
    });

    updateSchemas({
      date_set_value: {
        default_at: defaultDate,
        start_at: startDate,
        end_at: endDate,
      },
    });
  };

  return (
    <FormWrapper>
      <LabeledTextBox
        labelText="項目名"
        value={schema.field_label}
        onBlur={e => updateSchema('field_label', e.target.value)}
        required
      />
      <LabeledSelect
        labelText="回答形式"
        options={formTypeOptions}
        value={selectedFormType}
        onChange={onSelectFormType}
      />
      {selectedFormType.value === 'radio' || selectedFormType.value === 'checkbox'
       ? (
         <SetOptionsForm
           addOptions={addOptions}
           editOptions={editOptions}
           deleteOptions={deleteOptions}
           schema={schema}
           formType={selectedFormType.value}
           defaultValue={schema.default}
           updateSchema={updateSchema}
           setSchema={setSchema}
         />
       )
       : (
         <details>
           <summary>設定項目</summary>
           {isTextFormType(selectedFormType.value)
            ? (
              <div>
                <LabeledTextBox
                  labelText="初期入力値"
                  value={schema.default}
                  onBlur={e => updateSchema('default', e.target.value)}
                  pattern={regex}
                />
                {selectedFormType.value === 'number' || selectedFormType.value === 'integer'
                 ? (
                   <div>
                     <LabeledTextBox
                       labelText="最小値"
                       value={!(schema.number_range == null) ? schema.number_range.min : null}
                       onBlur={e => handleNumberRange('min', e.target.value)}
                       pattern={regex}
                     />
                     <LabeledTextBox
                       labelText="最大値"
                       value={!(schema.number_range == null) ? schema.number_range.max : null}
                       onBlur={e => handleNumberRange('max', e.target.value)}
                       pattern={regex}
                     />
                   </div>
                 )
                 : null}
                <LabeledTextBox
                  labelText="入力欄ラベル"
                  value={schema.prefix_label}
                  onBlur={e => updateSchema('prefix_label', e.target.value)}
                />
                <LabeledTextBox
                  labelText="プレースホルダ"
                  value={schema.placeholder}
                  onBlur={e => updateSchema('placeholder', e.target.value)}
                />
                {selectedFormType.value === 'text'
                 ? (
                   <div>
                     <LabeledTextBox
                       labelText="右記の文言を含む"
                       value={includeText}
                       onBlur={e => onSetIncludeText(e.target.value)}
                     />
                     <div>　カンマ区切りで複数選択できます（例）aaa,bbb,ccc</div>
                     <LabeledTextBox
                       labelText="右記の文言を含まない"
                       value={notIncludeText}
                       onBlur={e => onSetNotIncludeText(e.target.value)}
                     />
                     <div>　カンマ区切りで複数選択できます（例）aaa,bbb,ccc</div>
                   </div>
                 )
                 : null }
                {selectedFormType.value === 'regex'
                 ? (
                   <div>
                     <LabeledTextBox
                       labelText="正規表現"
                       value={schema.regex}
                       onBlur={e => updateSchema('regex', e.target.value)}
                     />
                     <LabeledTextBox
                       labelText="正規表現違反時エラー文言"
                       value={schema.regex_error_message_ja}
                       onBlur={e => updateSchema('regex_error_message_ja', e.target.value)}
                     />
                     <LabeledTextBox
                       labelText="正規表現違反時エラー文言（英語：半角英数字）"
                       value={getRegexErrMessageEn}
                       onBlur={e => updateSchema('regex_error_message_en', e.target.value)}
                       pattern="^[0-9A-Za-z _-]+$"
                     />
                   </div>
                 )
                 : null}
                <LabeledCheckBox
                  labelText="必須項目"
                  checked={isRequired}
                  setChecked={onCheckRequired}
                />
                {isRequired
                 ? (
                   <Labeled labelText="必須事由" required >
                     <TextArea
                       value={schema.required_reason}
                       onChange={e => updateSchema('required_reason', e.target.value)}
                       required
                     />
                   </Labeled>
                 )
                 : null}
              </div>
            )
            : null}
           {selectedFormType.value === 'date_select'
            ? (
              <div>
                <Labeled labelText="日付の初期設定値" >
                  <DateTime
                    value={defaultDate}
                    onChange={handleDefaultDate}
                  />
                </Labeled>
                <Labeled labelText="設定できる日付の範囲" >
                  <DateTimeRange
                    dateRange={dateRange}
                    setDateRange={handleDateRange}
                  />
                </Labeled>
                <LabeledCheckBox
                  labelText="必須項目"
                  checked={isRequired}
                  setChecked={onCheckRequired}
                />
                {isRequired
                 ? (
                   <Labeled labelText="必須事由" required >
                     <TextArea
                       value={schema.required_reason}
                       onChange={e => updateSchema('required_reason', e.target.value)}
                       required
                     />
                   </Labeled>
                 )
                 : null}
              </div>
            )
            : null}
           {selectedFormType.value === 'prefectures'
            ? (
              <div>
                <LabeledSelect
                  labelText="初期設定値"
                  options={prefecturesOptions}
                  value={prefecturesOptions.find(v => v.value === schema.default)}
                  onChange={v => updateSchema('default', v.value)}
                />
              </div>
            )
            : null}
         </details>
       )}
      <div style={{ display: 'inline-flex' }} >
        <Button onClick={e => handleChangeOrder(e, 'up')}>上へ</Button>
        <Button onClick={e => handleChangeOrder(e, 'down')}>下へ</Button>
        <Button style={{ backgroundColor: 'red' }} onClick={e => handleDelete(e)} >削除</Button>
      </div>
    </FormWrapper>
  );
};

InputForm.propTypes = {
  index: PropTypes.number.isRequired,
  schema: PropTypes.object.isRequired,
  setSchema: PropTypes.func.isRequired,
  moveUpSchema: PropTypes.func.isRequired,
  moveDownSchema: PropTypes.func.isRequired,
  deleteSchema: PropTypes.func.isRequired,
};

export default InputForm;
